import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { API_HOST } from '../../utils/api';
import { ExportActions, QualityReport } from '../shared/AssetExtras';
import {
  HiOutlineDocumentText, HiOutlineMail, HiOutlineCollection, HiOutlineDocument,
  HiOutlinePhotograph, HiOutlineGlobe, HiOutlinePresentationChartBar, HiOutlineDownload, HiOutlineEye,
  HiOutlineChartBar
} from 'react-icons/hi';

const ASSET_TYPES = {
  blog: { label: 'Blog Post', icon: HiOutlineDocumentText, format: 'DOCX' },
  newsletter: { label: 'Newsletter', icon: HiOutlineMail, format: 'HTML' },
  email_sequence: { label: 'Email Sequence', icon: HiOutlineCollection, format: 'DOCX' },
  one_pager: { label: 'One-Pager', icon: HiOutlineDocument, format: 'HTML' },
  banner: { label: 'Banner', icon: HiOutlinePhotograph, format: 'HTML' },
  service_page: { label: 'Service Page', icon: HiOutlineGlobe, format: 'HTML' },
  presentation: { label: 'Presentation', icon: HiOutlinePresentationChartBar, format: 'HTML' },
  marketing_strategy: { label: 'Marketing Strategy', icon: HiOutlineChartBar, format: 'HTML', badge: 'Strategy' },
};

const CURRENT_MARKETING_OPTIONS = [
  'Google Ads', 'Facebook/Meta Ads', 'LinkedIn Ads', 'SEO/Content Marketing',
  'Email Marketing', 'Cold Outreach', 'Events/Webinars', 'Referral Program',
  'Trade Shows', 'PR/Media', 'None / Just Starting'
];

const TARGET_INDUSTRIES = [
  'Accounting & Finance', 'Architecture & Engineering', 'Construction', 'Education',
  'Government', 'Healthcare', 'Insurance', 'Legal', 'Manufacturing',
  'Media & Entertainment', 'Non-Profit', 'Real Estate', 'Retail & E-Commerce',
  'Software & Technology', 'Staffing & Recruiting', 'Telecommunications',
  'Transportation & Logistics', 'Other'
];

const COMPANY_SIZES = [
  '1-10 employees', '11-50 employees', '51-200 employees',
  '201-500 employees', '501-1000 employees', '1000+ employees'
];

const PRIMARY_GOALS = [
  'Lead Generation', 'Brand Awareness', 'Customer Retention',
  'Market Expansion', 'Product Adoption', 'Thought Leadership'
];

const BUDGET_RANGES = [
  'Under $1,000/mo', '$1,000-$2,500/mo', '$2,500-$5,000/mo',
  '$5,000-$10,000/mo', '$10,000-$25,000/mo', '$25,000+/mo'
];

const PROGRESS_MESSAGES = [
  'Analyzing your brand profile...',
  'Researching topic context...',
  'Crafting your content...',
  'Applying your branding...',
  'Formatting the final document...',
  'Running quality checks...',
  'Almost there, polishing the details...',
];

const BANNER_SIZE_GROUPS = [
  { label: 'Matching sets', options: [
    { value: 'display-set', label: 'Display ad set (728x90, 300x250, 160x600, 320x50)' },
    { value: 'social-set', label: 'Social set (1200x627, 1080x1080, LinkedIn cover 1128x191)' },
  ] },
  { label: 'Display ads', options: [
    { value: '728x90', label: '728x90 (Leaderboard)' },
    { value: '300x250', label: '300x250 (Medium Rectangle)' },
    { value: '160x600', label: '160x600 (Wide Skyscraper)' },
    { value: '320x50', label: '320x50 (Mobile Banner)' },
  ] },
  { label: 'Social & email', options: [
    { value: '1200x627', label: '1200x627 (LinkedIn / Facebook post)' },
    { value: '1080x1080', label: '1080x1080 (Square social post)' },
    { value: '1128x191', label: '1128x191 (LinkedIn company cover)' },
    { value: '600x200', label: '600x200 (Email header / signature)' },
  ] },
];

const SEQUENCE_TYPES = [
  { value: 'nurture', label: 'Inbound nurture', hint: 'People who signed up or asked for information' },
  { value: 'cold_outbound', label: 'Cold outbound', hint: 'Prospects you have no relationship with yet' },
  { value: 'onboarding', label: 'Customer onboarding', hint: 'New customers who just signed' },
  { value: 'reengagement', label: 'Re-engagement', hint: 'Past leads or customers who went quiet' },
];

const MEETING_TYPES = [
  { value: 'intro', label: 'Intro / discovery' },
  { value: 'proposal', label: 'Solution proposal' },
  { value: 'educational', label: 'Educational session' },
];

const NOTES_PLACEHOLDERS = {
  blog: 'e.g., Aim at accounting firms in Ohio; mention our free onboarding call; keep it practical.',
  newsletter: 'e.g., This is our March newsletter; promote our upcoming office hours.',
  email_sequence: 'e.g., Targeting dental practices; offer a 20-minute IT review.',
  one_pager: 'e.g., For a trade show booth; emphasize security for law firms.',
  banner: 'e.g., LinkedIn campaign for construction firms; CTA should be "Book a Demo".',
  service_page: 'e.g., Target Toronto small businesses; primary keyword "cloud desktops Toronto".',
  presentation: 'e.g., Our pricing is $X per user per month; they currently use on-site servers.',
  marketing_strategy: 'Anything else we should know about your business or goals.',
};

export default function AssetGenerator() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const [extraOptions, setExtraOptions] = useState({});
  const [generating, setGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [progressMsgIdx, setProgressMsgIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const resultFrameRef = useRef(null);

  // Load topics when type is selected
  useEffect(() => {
    if (selectedType && selectedType !== 'marketing_strategy') {
      api.get(`/topics?assetType=${selectedType}`).then(({ data }) => setTopics(data)).catch(console.error);
    }
  }, [selectedType]);

  // Poll for status
  useEffect(() => {
    if (!jobId || !generating) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/assets/${jobId}/status`);
        if (data.status === 'completed') {
          setGenerating(false);
          setResult(data);
          clearInterval(interval);
        } else if (data.status === 'failed') {
          setGenerating(false);
          setError(data.error ? `Generation failed: ${data.error}` : 'Generation failed. Please try again.');
          clearInterval(interval);
        }
      } catch {
        // continue polling
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [jobId, generating]);

  // Rotate progress messages
  useEffect(() => {
    if (!generating) return;
    const interval = setInterval(() => {
      setProgressMsgIdx(prev => (prev + 1) % PROGRESS_MESSAGES.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [generating]);

  const handleGenerate = async () => {
    setError('');
    setGenerating(true);
    setProgressMsgIdx(0);
    setResult(null);
    try {
      const payload = {
        assetType: selectedType,
        isWhitelabel,
        extraOptions: Object.keys(extraOptions).length > 0 ? extraOptions : undefined,
      };
      // Only include topicId for asset types that need one (marketing_strategy is self-contained)
      if (selectedType !== 'marketing_strategy') {
        payload.topicId = selectedTopic;
      }
      const { data } = await api.post('/assets/generate', payload);
      setJobId(data.id);
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.error || 'Failed to start generation');
    }
  };

  const handleRegenerate = async () => {
    if (!feedbackText.trim()) return;
    setShowFeedback(false);
    setGenerating(true);
    setProgressMsgIdx(0);
    setResult(null);
    setError('');
    try {
      const { data } = await api.post(`/assets/${jobId}/regenerate`, { feedback: feedbackText });
      setJobId(data.id);
      setFeedbackText('');
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.error || 'Failed to regenerate');
    }
  };

  const reset = () => {
    setStep(1);
    setSelectedType('');
    setSelectedTopic('');
    setIsWhitelabel(false);
    setExtraOptions({});
    setJobId(null);
    setResult(null);
    setError('');
    setShowFeedback(false);
    setFeedbackText('');
  };

  // Step 1: Select type
  if (step === 1 && !generating && !result) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Generate an Asset</h1>
        <p className="text-gray-500 mb-8">Select the type of marketing asset you want to create.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(ASSET_TYPES).map(([type, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={type}
                onClick={() => { setSelectedType(type); setStep(2); }}
                className={`p-6 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                  selectedType === type ? 'border-v2-blue bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <Icon className="w-8 h-8 text-v2-blue mb-3" />
                <div className="font-medium text-gray-900">{config.label}</div>
                <div className="text-xs text-gray-400 mt-1">{config.format}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Step 2: Configure and generate
  if (step === 2 && !generating && !result) {
    return (
      <div className="max-w-2xl">
        <button onClick={() => setStep(1)} className="text-v2-blue hover:underline text-sm mb-4 inline-block">&larr; Back to asset types</button>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">
          Generate {ASSET_TYPES[selectedType]?.label}
        </h1>
        <p className="text-gray-500 mb-6">Configure your asset and click generate.</p>

        <div className="space-y-4">
          {/* Topic selection - hidden for marketing_strategy */}
          {selectedType !== 'marketing_strategy' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select a Topic <span className="text-red-500">*</span></label>
              {topics.length === 0 ? (
                <p className="text-gray-400 text-sm">No topics available for this asset type.</p>
              ) : (
                <div className="space-y-2">
                  {topics.map(topic => (
                    <label key={topic.id} className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTopic === topic.id ? 'border-v2-blue bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="radio"
                          name="topic"
                          value={topic.id}
                          checked={selectedTopic === topic.id}
                          onChange={() => setSelectedTopic(topic.id)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{topic.title}</div>
                          <div className="text-sm text-gray-500 mt-1">{topic.description}</div>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Branding toggle - hidden for marketing_strategy */}
          {selectedType !== 'marketing_strategy' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">Branding Mode</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsWhitelabel(false)}
                  className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    !isWhitelabel ? 'border-v2-blue bg-blue-50 text-v2-blue' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Co-branded with V2 Cloud
                </button>
                <button
                  onClick={() => setIsWhitelabel(true)}
                  className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    isWhitelabel ? 'border-v2-blue bg-blue-50 text-v2-blue' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  White Label
                </button>
              </div>
            </div>
          )}

          {/* Extra options */}
          {selectedType === 'email_sequence' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sequence Type</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {SEQUENCE_TYPES.map(t => {
                    const active = (extraOptions.sequenceType || 'nurture') === t.value;
                    return (
                      <button key={t.value} type="button"
                        onClick={() => setExtraOptions({ ...extraOptions, sequenceType: t.value })}
                        className={`text-left p-3 rounded-lg border-2 transition-all ${active ? 'border-v2-blue bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <div className={`text-sm font-medium ${active ? 'text-v2-blue' : 'text-gray-800'}`}>{t.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{t.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sequence Length</label>
                <select
                  value={extraOptions.sequenceLength || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, sequenceLength: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                >
                  <option value="">Select length</option>
                  {[3, 4, 5, 6, 7].map(n => <option key={n} value={n}>{n} emails</option>)}
                </select>
              </div>
            </div>
          )}

          {selectedType === 'banner' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Banner Size</label>
              <select
                value={extraOptions.bannerSize || ''}
                onChange={e => setExtraOptions({ ...extraOptions, bannerSize: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
              >
                <option value="">Select a size or a matching set</option>
                {BANNER_SIZE_GROUPS.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-2">A matching set creates every size with the same message and design, and counts as one banner. You can download each size as a PNG.</p>
            </div>
          )}

          {selectedType === 'presentation' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Who is this for? <span className="text-xs text-gray-400 font-normal">(optional)</span></h3>
                <p className="text-xs text-gray-400">Tailors the title slide, pain points, and pitch to the meeting.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" value={extraOptions.prospectCompany || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, prospectCompany: e.target.value })}
                  placeholder="Prospect company (e.g., Smith & Co. CPAs)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
                <input type="text" value={extraOptions.prospectIndustry || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, prospectIndustry: e.target.value })}
                  placeholder="Their industry (e.g., Accounting)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {MEETING_TYPES.map(m => {
                  const active = extraOptions.meetingType === m.value;
                  return (
                    <button key={m.value} type="button"
                      onClick={() => setExtraOptions({ ...extraOptions, meetingType: active ? undefined : m.value })}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${active ? 'border-v2-blue bg-blue-50 text-v2-blue font-medium' : 'border-gray-200 text-gray-700 hover:border-gray-300'}`}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {selectedType === 'marketing_strategy' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Marketing Strategy Questionnaire</h3>
                <p className="text-sm text-gray-500">Fields marked with <span className="text-red-500">*</span> are required. The more detail you provide, the more tailored your strategy will be.</p>
              </div>

              {/* Strategy Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Strategy Duration <span className="text-red-500">*</span></label>
                <div className="flex gap-3">
                  {[3, 6].map(months => (
                    <button
                      key={months}
                      type="button"
                      onClick={() => setExtraOptions({ ...extraOptions, strategyDuration: months })}
                      className={`flex-1 p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                        extraOptions.strategyDuration === months
                          ? 'border-v2-blue bg-blue-50 text-v2-blue'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {months} Months
                    </button>
                  ))}
                </div>
              </div>

              {/* Primary Goals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary Goals <span className="text-red-500">*</span> <span className="text-xs text-gray-400 font-normal">(select all that apply)</span></label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {PRIMARY_GOALS.map(goal => {
                    const selected = (extraOptions.primaryGoals || []).includes(goal);
                    return (
                      <button
                        key={goal}
                        type="button"
                        onClick={() => {
                          const current = extraOptions.primaryGoals || [];
                          const updated = selected ? current.filter(c => c !== goal) : [...current, goal];
                          setExtraOptions({ ...extraOptions, primaryGoals: updated });
                        }}
                        className={`text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                          selected
                            ? 'border-v2-blue bg-blue-50 text-v2-blue font-medium'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {goal}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Current Marketing Activities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Current Marketing Activities <span className="text-red-500">*</span> <span className="text-xs text-gray-400 font-normal">(select all that apply)</span></label>
                <div className="grid grid-cols-2 gap-2">
                  {CURRENT_MARKETING_OPTIONS.map(opt => {
                    const selected = (extraOptions.currentMarketing || []).includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const current = extraOptions.currentMarketing || [];
                          const updated = selected ? current.filter(c => c !== opt) : [...current, opt];
                          setExtraOptions({ ...extraOptions, currentMarketing: updated });
                        }}
                        className={`text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                          selected
                            ? 'border-v2-blue bg-blue-50 text-v2-blue font-medium'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Channel-specific questions - shown only if that channel is selected */}
              {(extraOptions.currentMarketing || []).some(c => ['Google Ads', 'Facebook/Meta Ads', 'LinkedIn Ads', 'SEO/Content Marketing', 'Email Marketing', 'Cold Outreach', 'Events/Webinars', 'Referral Program'].includes(c)) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900">Tell us more about your active channels</h4>

                  {(extraOptions.currentMarketing || []).includes('Google Ads') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Google Ads Spend & Results</label>
                      <textarea
                        value={extraOptions.googleAdsDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, googleAdsDetails: e.target.value })}
                        placeholder="e.g., Spending $2,000/mo, getting ~15 leads at $130 CPL, conversion rate is 8%"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('Facebook/Meta Ads') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Facebook/Meta Ads Spend & Results</label>
                      <textarea
                        value={extraOptions.metaAdsDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, metaAdsDetails: e.target.value })}
                        placeholder="e.g., $800/mo, mostly retargeting, 3% CTR, 5 conversions/mo"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('LinkedIn Ads') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Ads Spend & Results</label>
                      <textarea
                        value={extraOptions.linkedinAdsDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, linkedinAdsDetails: e.target.value })}
                        placeholder="e.g., $1,500/mo, sponsored content + InMail, 2 SQLs/mo"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('SEO/Content Marketing') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SEO / Content Marketing Details</label>
                      <textarea
                        value={extraOptions.seoContentDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, seoContentDetails: e.target.value })}
                        placeholder="e.g., Publish 2 blogs/mo, ranking for 5 keywords, 1,500 organic visits/mo, top topics are X and Y"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('Email Marketing') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Marketing Details</label>
                      <textarea
                        value={extraOptions.emailMarketingDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, emailMarketingDetails: e.target.value })}
                        placeholder="e.g., 3,500 subscribers, monthly newsletter, 22% open rate, 3% CTR"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('Cold Outreach') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cold Outreach Details</label>
                      <textarea
                        value={extraOptions.coldOutreachDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, coldOutreachDetails: e.target.value })}
                        placeholder="e.g., 200 cold emails/wk via Apollo, 4% reply rate, 1 booked meeting per week"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('Events/Webinars') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Events / Webinars Details</label>
                      <textarea
                        value={extraOptions.eventsDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, eventsDetails: e.target.value })}
                        placeholder="e.g., One webinar per quarter, 80 registrants avg, 30% attendance, 5 demo requests per webinar"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}

                  {(extraOptions.currentMarketing || []).includes('Referral Program') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Referral Program Details</label>
                      <textarea
                        value={extraOptions.referralDetails || ''}
                        onChange={e => setExtraOptions({ ...extraOptions, referralDetails: e.target.value })}
                        placeholder="e.g., Customers earn $500 per referral, ~3 referrals/mo, 50% close rate"
                        rows={2}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical bg-white"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Monthly Budget */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monthly Marketing Budget <span className="text-red-500">*</span></label>
                <select
                  value={extraOptions.monthlyBudget || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, monthlyBudget: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                >
                  <option value="">Select budget range</option>
                  {BUDGET_RANGES.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              {/* Geographic Target Areas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Geographic Target Areas</label>
                <input
                  type="text"
                  value={extraOptions.targetAreas || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, targetAreas: e.target.value })}
                  placeholder="e.g., Texas, California, Greater Toronto Area, US Northeast"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                />
              </div>

              {/* Target Industries */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Customer Industries (select up to 5)</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {TARGET_INDUSTRIES.map(ind => {
                    const selected = (extraOptions.targetIndustries || []).includes(ind);
                    const atLimit = !selected && (extraOptions.targetIndustries || []).length >= 5;
                    return (
                      <button
                        key={ind}
                        type="button"
                        disabled={atLimit}
                        onClick={() => {
                          const current = extraOptions.targetIndustries || [];
                          const updated = selected ? current.filter(c => c !== ind) : [...current, ind];
                          setExtraOptions({ ...extraOptions, targetIndustries: updated });
                        }}
                        className={`text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                          selected
                            ? 'border-v2-blue bg-blue-50 text-v2-blue font-medium'
                            : atLimit
                            ? 'border-gray-100 text-gray-300 cursor-not-allowed'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {ind}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target Company Sizes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Company Sizes</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {COMPANY_SIZES.map(size => {
                    const selected = (extraOptions.targetCompanySizes || []).includes(size);
                    return (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          const current = extraOptions.targetCompanySizes || [];
                          const updated = selected ? current.filter(c => c !== size) : [...current, size];
                          setExtraOptions({ ...extraOptions, targetCompanySizes: updated });
                        }}
                        className={`text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                          selected
                            ? 'border-v2-blue bg-blue-50 text-v2-blue font-medium'
                            : 'border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Current Performance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Monthly Leads</label>
                  <input
                    type="text"
                    value={extraOptions.currentMonthlyLeads || ''}
                    onChange={e => setExtraOptions({ ...extraOptions, currentMonthlyLeads: e.target.value })}
                    placeholder="e.g., 25"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Average Customer Value</label>
                  <input
                    type="text"
                    value={extraOptions.avgCustomerValue || ''}
                    onChange={e => setExtraOptions({ ...extraOptions, avgCustomerValue: e.target.value })}
                    placeholder="e.g., $5,000/year"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sales Cycle Length</label>
                  <input
                    type="text"
                    value={extraOptions.salesCycle || ''}
                    onChange={e => setExtraOptions({ ...extraOptions, salesCycle: e.target.value })}
                    placeholder="e.g., 30-60 days"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Top Competitors</label>
                  <input
                    type="text"
                    value={extraOptions.competitors || ''}
                    onChange={e => setExtraOptions({ ...extraOptions, competitors: e.target.value })}
                    placeholder="e.g., Competitor A, Competitor B"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none"
                  />
                </div>
              </div>

              {/* Open Text Questions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Biggest Marketing Challenge Right Now</label>
                <textarea
                  value={extraOptions.biggestChallenge || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, biggestChallenge: e.target.value })}
                  placeholder="e.g., Hard to break into the legal vertical, leads are too low quality, can't compete on price..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What's Worked Before</label>
                  <textarea
                    value={extraOptions.workedBefore || ''}
                    onChange={e => setExtraOptions({ ...extraOptions, workedBefore: e.target.value })}
                    placeholder="What past marketing efforts produced results?"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What Hasn't Worked</label>
                  <textarea
                    value={extraOptions.didntWork || ''}
                    onChange={e => setExtraOptions({ ...extraOptions, didntWork: e.target.value })}
                    placeholder="What didn't move the needle?"
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Available Internal Marketing Resources</label>
                <textarea
                  value={extraOptions.internalResources || ''}
                  onChange={e => setExtraOptions({ ...extraOptions, internalResources: e.target.value })}
                  placeholder="e.g., 1 part-time marketing coordinator, owner does all marketing, agency on retainer..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
                />
              </div>

              {/* V2 Cloud Execution Interest */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interested in V2 Cloud Helping Execute This Strategy?</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'yes', label: 'Yes', subtitle: 'Tell me how' },
                    { value: 'maybe', label: 'Maybe', subtitle: 'Show me options' },
                    { value: 'no', label: 'No', subtitle: 'Self-execute' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setExtraOptions({ ...extraOptions, v2CloudExecutionInterest: opt.value })}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        extraOptions.v2CloudExecutionInterest === opt.value
                          ? 'border-v2-blue bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`text-sm font-medium ${
                        extraOptions.v2CloudExecutionInterest === opt.value ? 'text-v2-blue' : 'text-gray-700'
                      }`}>{opt.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{opt.subtitle}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes for this asset <span className="text-xs text-gray-400 font-normal">(optional)</span></label>
            <p className="text-xs text-gray-400 mb-2">Audience, offer, keywords, tone, or facts you want included. The AI only uses facts you give it or that come from your profile.</p>
            <textarea
              value={extraOptions.notes || ''}
              onChange={e => setExtraOptions({ ...extraOptions, notes: e.target.value.slice(0, 2000) })}
              rows={3}
              placeholder={NOTES_PLACEHOLDERS[selectedType] || ''}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
          )}

          <button
            onClick={handleGenerate}
            disabled={
              (selectedType !== 'marketing_strategy' && !selectedTopic)
              || (selectedType === 'email_sequence' && !extraOptions.sequenceLength)
              || (selectedType === 'banner' && !extraOptions.bannerSize)
              || (selectedType === 'marketing_strategy' && (
                  !extraOptions.strategyDuration
                  || !(extraOptions.primaryGoals?.length)
                  || !(extraOptions.currentMarketing?.length)
                  || !extraOptions.monthlyBudget
                ))
            }
            className="w-full bg-v2-blue text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Generate {ASSET_TYPES[selectedType]?.label}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (generating) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="mb-8">
          <div className="w-16 h-16 border-4 border-v2-blue border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Generating your {ASSET_TYPES[selectedType]?.label}</h2>
        <p className="text-v2-blue font-medium mb-2 flex items-center justify-center gap-2">
          <span className="w-2 h-2 bg-v2-blue rounded-full animate-pulse" />
          {PROGRESS_MESSAGES[progressMsgIdx]}
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
          <div className="h-2 bg-v2-blue rounded-full animate-progress" />
        </div>
        <p className="text-sm text-gray-400">
          This typically takes 1-3 minutes{['blog', 'newsletter'].includes(selectedType) ? ' (we research current, citable sources first)' : ''}. Please don't refresh the page.
        </p>
      </div>
    );
  }

  // Result
  if (result) {
    const previewUrl = `${API_HOST}/api/assets/${result.id}/preview`;
    const downloadUrl = `${API_HOST}/api/assets/${result.id}/download`;
    const canPreview = ['html', 'docx', 'pdf'].includes(result.fileFormat);

    return (
      <div className="max-w-5xl mx-auto">
        {/* Preview area */}
        {result.filePath && canPreview && (
          <div className="bg-white rounded-xl border border-gray-200 mb-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
              <span className="text-sm font-medium text-gray-700">Preview</span>
              <div className="flex items-center gap-2">
                <a href={previewUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-v2-blue hover:underline flex items-center gap-1">
                  <HiOutlineEye className="w-3.5 h-3.5" /> Open full preview
                </a>
              </div>
            </div>
            <div className="h-[500px]">
              <iframe ref={resultFrameRef} src={previewUrl} className="w-full h-full border-0" title="Asset Preview" sandbox="allow-same-origin allow-modals" />
            </div>
          </div>
        )}

        {/* Actions bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Asset Generated Successfully!</h2>
              <p className="text-sm text-gray-500">Your {ASSET_TYPES[selectedType]?.label} is ready.</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            {result.filePath && (
              <a href={downloadUrl}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700">
                <HiOutlineDownload className="w-5 h-5" />
                Download {result.fileFormat?.toUpperCase()}
              </a>
            )}
            {result.filePath && (
              <ExportActions
                assetType={selectedType}
                assetId={result.id}
                previewUrl={previewUrl}
                onPrint={() => resultFrameRef.current?.contentWindow?.print()}
              />
            )}
            <button onClick={() => setShowFeedback(true)}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">
              Request Changes
            </button>
            <button onClick={reset}
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">
              Generate Another
            </button>
          </div>

          {result.qualityReport && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <QualityReport report={result.qualityReport} />
            </div>
          )}

          {showFeedback && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Describe the changes you'd like:</label>
              <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical"
                placeholder="e.g., Make the tone more professional, add a section about pricing..." />
              <button onClick={handleRegenerate} disabled={!feedbackText.trim()}
                className="mt-3 px-6 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                Regenerate with Changes
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}
